import React, { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useUserContext } from 'src/context/user';
import { DEV_TOOL_USE_2025_EXPERIENCE_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { useDevToolPermissions } from 'src/apps/Tools/DevTools/utils';

export type ExperienceType = 'classic' | '2024' | '2025';

export type ExperienceContextValue = {
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
};

const defaultValue: ExperienceContextValue = {
  isLoaded: false,
  isError: false,
  experienceType: null,
};

export const ExperienceContext = React.createContext<{
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
}>(defaultValue);

export const useExperienceContext = () => useContext(ExperienceContext);

export const ExperienceProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { user, error } = useUserContext();
  const areDevToolsAllowed = useDevToolPermissions();
  const [experienceType, setExperienceType] = useState<ExperienceType | null>(
    defaultValue.experienceType,
  );
  const experience = useMemo(
    () => ({
      isLoaded: experienceType !== null,
      isError: !!error,
      experienceType,
    }),
    [error, experienceType],
  );

  useEffect(() => {
    if (!user?.id) return;

    if (areDevToolsAllowed) {
      const useWordlists =
        getLocalStorageEntry(DEV_TOOL_USE_2025_EXPERIENCE_STORAGE_KEY) === 'true';
      if (useWordlists) {
        setExperienceType('2025');
        return;
      }
    }

    setExperienceType('2024');
  }, [user?.id, areDevToolsAllowed]);

  return <ExperienceContext.Provider value={experience}>{children}</ExperienceContext.Provider>;
};
